import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, ChangeEvent } from "react";
import { Formik, Field, FormikProps, FormikValues } from "formik";
import { useSelector } from "react-redux";
import classNames from "classnames";
import BeatLoader from "react-spinners/BeatLoader";
import decryptData from "../decrypt";
import {
  fetchClientsByAgentCodes,
  hasMultipleCodes,
  splitAgentCodes,
} from "../services/userService";
import { handleLogout } from "../config/axios";
import ReactSelect from "react-select";

// --- Type Definitions ---
interface Address {
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
  tel: string;
  email: string;
  codice_indirizzo: string;
}

interface TrustBank {
  saldo_contabile: number;
  fido: number;
}

interface Discount {
  codice_sconto: string;
  descrizione: string;
  percentuale_sconto_1: string;
  percentuale_sconto_2: string;
  percentuale_sconto_3: string;
  percentuale_sconto_4: string;
  percentuale_sconto_5: string;
  percentuale_sconto_6: string;
}

interface FinancialSituation {
  valore: number;
  tipo: string;
}

interface FinancialData {
  bolle_in_attesa_di_fattura: number;
  ordini_programmati: number;
  ordini: number;
  esposizione_riba: number;
  cambiale: number;
  insoluti: number;
  titoli_a_rischio: number;
  titoli: number;
  altro: number;
}

interface Customer {
  ragione_sociale: string;
  codice_cliente: string;
  codice_agente: string;
  indirizzo_cliente?: string;
  localita_cliente?: string;
  cap_cliente?: string;
  provincia_cliente?: string;
  telefono_cliente?: string;
  email_cliente?: string;
  pagamento?: string;
  addresses: Address[];
  trustBank?: TrustBank;
  discounts?: Discount[];
  financialSituation?: FinancialSituation[];
  financialData?: FinancialData;
}

interface CustomerDetailAttribute {
  key: string;
  label: string;
  type: string;
  value: string;
  lookup_table: string | null;
  resolved_value: string;
}

interface LookupOption {
  id: number;
  value: string;
}

type LookupMap = Record<string, LookupOption[]>;

interface AttributeDefinition {
  attribute_key: string;
  attribute_label: string;
  attribute_type: string;
  lookup_table: string | null;
}

const ATTRIBUTE_GROUPS: { label: string; keys: string[] }[] = [
  { label: "Tipologia", keys: ["tipologia_cliente"] },
  { label: "Marche Cucina", keys: ["marca_cucina_1", "marca_cucina_2", "marca_cucina_3"] },
  { label: "Marche Elettrodomestici", keys: ["marca_1_elettrodomestici", "marca_elettrodomestici_2", "marca_elettrodomestici_3"] },
  { label: "Distributori", keys: ["distributore_1", "distributore_2", "distributore_3"] },
  { label: "Statistiche", keys: ["numero_di_cucine_esposte", "stima_cucine_vendute_anno", "numero_venditori"] },
  { label: "Valutazioni", keys: ["valutazione_commerciale", "valutazione_finanziaria_amministrativa"] },
  { label: "Note", keys: ["commento"] }
];

// Minimal Redux state typing for the theme
interface MenuState {
  theme: "dark" | "light";
}

interface RootState {
  menu: MenuState;
}

// --- Utility Functions ---
/**
 * Debounce: delays the execution of a function until after delay milliseconds have elapsed.
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  }) as T;
}

/**
 * Parse a string percentage to a decimal number.
 */
const parsePerc = (value: string): number => {
  if (!value || value === ".00") return 0;
  return parseFloat(value) / 100;
};

// --- Main Component ---
export default function AnagraficaClientiDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [collapseActive, setCollapseActive] = useState<boolean>(false);
  const [secondCollapseActive, setSecondCollapseActive] =
    useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [inputValue, setInputValue] = useState<string>("");
  const formikRef = useRef<FormikProps<FormikValues>>(null);
  const [filteredDiscounts, setFilteredDiscounts] = useState<Discount[]>([]);
  const [globalLoading, setGlobalLoading] = useState<boolean>(true);

  // Customer Detail (Dettaglio Commerciale) state
  const [customerDetailAttrs, setCustomerDetailAttrs] = useState<CustomerDetailAttribute[]>([]);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailFetched, setDetailFetched] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [lookups, setLookups] = useState<LookupMap>({});
  const [allDefinitions, setAllDefinitions] = useState<AttributeDefinition[]>([]);
  const [addingNew, setAddingNew] = useState<boolean>(false);
  const [newAttrKey, setNewAttrKey] = useState<string>("");
  const [newAttrValue, setNewAttrValue] = useState<string>("");
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Get current theme from Redux store
  const currentTheme = useSelector((state: RootState) => state.menu.theme);
  const [theme, setTheme] = useState<"dark" | "light">(currentTheme);

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  // Re-initialize Preline after component renders (tabs need this)
  useEffect(() => {
    if (!globalLoading && customer) {
      window.HSStaticMethods?.autoInit();
    }
  }, [globalLoading, customer]);

  // Define base classes based on theme
  const baseClasses = {
    background: theme === "dark" ? "" : "bg-gray-50",
    card:
      theme === "dark"
        ? "bg-black-500 border-gray-700"
        : "bg-white border-gray-200",
    cardHeader: theme === "dark" ? "text-white" : "text-gray-900",
    text: theme === "dark" ? "text-gray-300" : "text-gray-900",
    textSecondary: theme === "dark" ? "text-gray-400" : "text-gray-600",
    highlight: theme === "dark" ? "text-blue-400" : "text-blue-600",
    hover: theme === "dark" ? "hover:bg-gray-700" : "hover:bg-gray-50",
    border: theme === "dark" ? "border-gray-700" : "border-gray-200",
    inputBg: theme === "dark" ? "" : "bg-gray-50",
  };

  // --- Data Fetching Functions ---
  const fetchCustomerDetails = async (): Promise<void> => {
    if (inputValue.length !== 0) return;
    try {
      setLoading(true);
      setGlobalLoading(true); // Imposta il caricamento globale

      // Fetch user info
      // In this case we're requesting by customer ID, not agent codes,
      // but still ensuring the proper format for any API calls
      const userResponse = await fetch("/nodeapi/utenti", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ codice_cliente: id }),
      });

      if (userResponse.status !== 200 && userResponse.status !== 201) {
        handleLogout(
          userResponse,
          "/nodeapi/utenti",
          JSON.stringify({ codice_cliente: id })
        );
        return;
      }

      const userData = await userResponse.json();

      const decrypted = decryptData(
        userData.encryptedData,
        userData.encryptedKey,
        userData.iv
      );
      const decryptedJson = JSON.parse(decrypted);

      if (!decryptedJson.data.length) {
        console.error("No user data found");
        return;
      }

      const user = decryptedJson.data[0];

      // Fetch addresses
      const addressResponse = await fetch("/nodeapi/indirizzi", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ codice_cliente: id }),
      });
      if (addressResponse.status !== 200 && addressResponse.status !== 201) {
        handleLogout(
          addressResponse,
          "/nodeapi/indirizzi",
          JSON.stringify({ codice_cliente: id })
        );
        return;
      }

      const addressData = await addressResponse.json();

      const decryptedAddr = decryptData(
        addressData.encryptedData,
        addressData.encryptedKey,
        addressData.iv
      );
      const decryptedJsonAddr = JSON.parse(decryptedAddr);

      // Fetch financial situation
      const financialResponse = await fetch("/nodeapi/situazione_finanziaria", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ codice_cliente: id }),
      });
      if (financialResponse.status !== 200 && financialResponse.status !== 201) {
        handleLogout(
          financialResponse,
          "/nodeapi/situazione_finanziaria",
          JSON.stringify({ codice_cliente: id })
        );
        return;
      }
      const financialData = await financialResponse.json();

      const decryptedFin = decryptData(
        financialData.encryptedData,
        financialData.encryptedKey,
        financialData.iv
      );
      const decryptedJsonFin = JSON.parse(decryptedFin);

      console.log("financialData: ", decryptedJsonFin);

      // Fetch discounts
      const discountResponse = await fetch("/nodeapi/sconti", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          codice_cliente: id,
          page: page,
        }),
      });
      if (discountResponse.status !== 200 && discountResponse.status !== 201) {
        handleLogout(
          discountResponse,
          "/nodeapi/sconti",
          JSON.stringify({
            codice_cliente: id,
            page: page,
          })
        );
        return;
      }
      const discountData = await discountResponse.json();

      // Handle both encrypted and non-encrypted responses
      let discountsData;
      if (discountData.encryptedData) {
        // If encrypted, decrypt the data
        const decryptedSconti = decryptData(
          discountData.encryptedData,
          discountData.encryptedKey,
          discountData.iv
        );
        discountsData = JSON.parse(decryptedSconti);
      } else {
        // If not encrypted, use the data directly
        discountsData = discountData;
      }

      // Set hasMore based on pagination
      setHasMore(discountsData.pagination?.hasMorePages || false);

      // Map discounts data
      const mappedDiscounts = discountsData.data.map((discount: any) => ({
        codice_sconto: discount.CODICE_SCONTO,
        descrizione: discount.DESCRIZIONE,
        percentuale_sconto_1: discount.PERCENTUALE_SCONTO_1,
        percentuale_sconto_2: discount.PERCENTUALE_SCONTO_2,
        percentuale_sconto_3: discount.PERCENTUALE_SCONTO_3,
        percentuale_sconto_4: discount.PERCENTUALE_SCONTO_4,
        percentuale_sconto_5: discount.PERCENTUALE_SCONTO_5,
        percentuale_sconto_6: discount.PERCENTUALE_SCONTO_6,
      }));

      // Map data to customer structure
      const newCustomer: Customer = {
        ragione_sociale: user.RAGIONE_SOCIALE,
        codice_cliente: user.CODICE_CLIENTE,
        codice_agente: user.CODICE_AGENTE,
        indirizzo_cliente: user.INDIRIZZO_CLIENTE,
        localita_cliente: user.LOCALITA_CLIENTE,
        cap_cliente: user.CAP_CLIENTE,
        provincia_cliente: user.PROVINCIA_CLIENTE,
        telefono_cliente: user.TELEFONO || user.TEL || user.TELEFONO_CLIENTE,
        email_cliente: user.EMAIL || user.EMAIL_CLIENTE,
        pagamento: user.PAGAMENTO,
        addresses: decryptedJsonAddr.data.map((addr: any) => ({
          indirizzo: addr.INDIRIZZO,
          cap: addr.CAP,
          citta: addr.CITTA,
          provincia: addr.PROVINCIA,
          tel: addr.TEL,
          email: addr.EMAIL,
          codice_indirizzo: addr.CODICE_INDIRIZZO,
        })),
        trustBank: {
          saldo_contabile: user.SALDO_CONTABILE,
          fido: user.FIDO,
        },
        financialSituation: decryptedJsonFin.data.map((item: any) => ({
          valore: item.VALORE,
          tipo: item.TIPO,
        })),
        financialData: {
          bolle_in_attesa_di_fattura:
            decryptedJsonFin.data.find(
              (item) => item.TIPO === "Bolle_in_attesa_di_fattura"
            )?.VALORE || 0,
          ordini_programmati:
            decryptedJsonFin.data.find(
              (item) => item.TIPO === "Ordini_programmati"
            )?.VALORE || 0,
          ordini:
            decryptedJsonFin.data.find((item) => item.TIPO === "Ordini")
              ?.VALORE || 0,
          esposizione_riba:
            decryptedJsonFin.data.find(
              (item) => item.TIPO === "Esposizione_riba"
            )?.VALORE || 0,
          cambiale:
            decryptedJsonFin.data.find((item) => item.TIPO === "Cambiale")
              ?.VALORE || 0,
          insoluti:
            decryptedJsonFin.data.find((item) => item.TIPO === "Insoluti")
              ?.VALORE || 0,
          titoli_a_rischio:
            decryptedJsonFin.data.find(
              (item) => item.TIPO === "Titoli_a_rischio"
            )?.VALORE || 0,
          titoli:
            decryptedJsonFin.data.find((item) => item.TIPO === "Titoli")
              ?.VALORE || 0,
          altro:
            decryptedJsonFin.data.find((item) => item.TIPO === "Altro")
              ?.VALORE || 0,
        },
      };

      if (customer && customer.discounts && page > 1) {
        // Append new discounts to existing ones
        setCustomer((prevCustomer) => ({
          ...prevCustomer!,
          discounts: [...(prevCustomer?.discounts || []), ...mappedDiscounts],
        }));
      } else {
        // Set the new customer with all data including discounts
        setCustomer((prevCustomer) => ({
          ...(prevCustomer || newCustomer), // Use prevCustomer if it exists, otherwise newCustomer
          ragione_sociale: newCustomer.ragione_sociale,
          codice_cliente: newCustomer.codice_cliente,
          codice_agente: newCustomer.codice_agente,
          indirizzo_cliente: newCustomer.indirizzo_cliente,
          localita_cliente: newCustomer.localita_cliente,
          cap_cliente: newCustomer.cap_cliente,
          provincia_cliente: newCustomer.provincia_cliente,
          telefono_cliente: newCustomer.telefono_cliente,
          email_cliente: newCustomer.email_cliente,
          addresses: newCustomer.addresses,
          trustBank: newCustomer.trustBank,
          financialSituation: newCustomer.financialSituation,
          financialData: newCustomer.financialData,
          discounts: mappedDiscounts,
        }));
      }

      setFilteredDiscounts(
        mappedDiscounts.filter((discount) =>
          discount.descrizione.toLowerCase().includes(inputValue.toLowerCase())
        )
      );
    } catch (error) {
      console.error("Error fetching customer details:", error);
    } finally {
      setLoading(false);
      setGlobalLoading(false); // Important: This sets globalLoading to false when done
    }
  };

  // Initial fetch and pagination update
  useEffect(() => {
    fetchCustomerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (page > 1) {
      fetchCustomerDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (customer?.discounts) {
      setFilteredDiscounts(
        customer.discounts.filter((discount) =>
          discount.descrizione.toLowerCase().includes(inputValue.toLowerCase())
        )
      );
    }
  }, [inputValue, customer?.discounts]);

  // Perform discount search request
  const performRequest = async (value: string): Promise<void> => {
    if (!value) return;
    try {
      setLoading(true);
      const response = await fetch("/nodeapi/sconti", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          codice_cliente: id,
          search: value,
        }),
      });

      if (response.status !== 200   && response.status !== 201) {
        handleLogout(
          response,
          "/nodeapi/sconti",
          JSON.stringify({
            codice_cliente: id,
            search: value,
          })
        );
        return;
      }

      const data = await response.json();

      if (customer) {
        setCustomer({
          ...customer,
          discounts: data.data.map((discount: any) => ({
            codice_sconto: discount.CODICE_SCONTO,
            descrizione: discount.DESCRIZIONE,
            percentuale_sconto_1: discount.PERCENTUALE_SCONTO_1,
            percentuale_sconto_2: discount.PERCENTUALE_SCONTO_2,
            percentuale_sconto_3: discount.PERCENTUALE_SCONTO_3,
            percentuale_sconto_4: discount.PERCENTUALE_SCONTO_4,
            percentuale_sconto_5: discount.PERCENTUALE_SCONTO_5,
            percentuale_sconto_6: discount.PERCENTUALE_SCONTO_6,
          })),
        });
      }
    } catch (error) {
      console.error("Error searching discounts:", error);
    } finally {
      setLoading(false);
      setGlobalLoading(false); // Ensure global loading is set to false
    }
  };

  // --- Customer Detail API Functions ---
  const fetchCustomerDetail = async (): Promise<void> => {
    try {
      setDetailLoading(true);
      const response = await fetch(`/nodeapi/customer-detail/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, `/nodeapi/customer-detail/${id}`, "");
        return;
      }

      const data = await response.json();
      const decrypted = decryptData(data.encryptedData, data.encryptedKey, data.iv);
      const parsed = JSON.parse(decrypted);

      setCustomerDetailAttrs(parsed.attributes || []);
      setDetailFetched(true);
    } catch (error) {
      console.error("Error fetching customer detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchLookups = async (): Promise<void> => {
    try {
      const response = await fetch("/nodeapi/customer-detail/lookups", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, "/nodeapi/customer-detail/lookups", "");
        return;
      }

      const data = await response.json();
      const decrypted = decryptData(data.encryptedData, data.encryptedKey, data.iv);
      const parsed = JSON.parse(decrypted);
      setLookups(parsed);
    } catch (error) {
      console.error("Error fetching lookups:", error);
    }
  };

  const saveAttribute = async (attributeKey: string, value: string): Promise<void> => {
    try {
      setSavingKeys((prev) => new Set(prev).add(attributeKey));
      const response = await fetch(`/nodeapi/customer-detail/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ [attributeKey]: value }),
      });

      if (response.status !== 200 && response.status !== 201) {
        handleLogout(
          response,
          `/nodeapi/customer-detail/${id}`,
          JSON.stringify({ [attributeKey]: value })
        );
        return;
      }

      setEditingKey(null);
      setEditValue("");
      await fetchCustomerDetail();
    } catch (error) {
      console.error("Error saving attribute:", error);
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.delete(attributeKey);
        return next;
      });
    }
  };

  const deleteAttribute = async (attributeKey: string): Promise<void> => {
    try {
      setSavingKeys((prev) => new Set(prev).add(attributeKey));
      const response = await fetch(`/nodeapi/customer-detail/${id}/${attributeKey}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        handleLogout(
          response,
          `/nodeapi/customer-detail/${id}/${attributeKey}`,
          ""
        );
        return;
      }

      await fetchCustomerDetail();
    } catch (error) {
      console.error("Error deleting attribute:", error);
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.delete(attributeKey);
        return next;
      });
    }
  };

  const fetchHistory = async (): Promise<void> => {
    try {
      setHistoryLoading(true);
      const response = await fetch(`/nodeapi/customer-detail/${id}/history`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });
      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, `/nodeapi/customer-detail/${id}/history`, "");
        return;
      }
      const data = await response.json();
      const decrypted = decryptData(data.encryptedData, data.encryptedKey, data.iv);
      setHistoryData(JSON.parse(decrypted));
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchDefinitions = async (): Promise<void> => {
    try {
      const response = await fetch("/nodeapi/customer-detail/definitions", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });

      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, "/nodeapi/customer-detail/definitions", "");
        return;
      }

      const data = await response.json();
      const decrypted = decryptData(data.encryptedData, data.encryptedKey, data.iv);
      const parsed = JSON.parse(decrypted);
      setAllDefinitions(parsed);
    } catch (error) {
      console.error("Error fetching definitions:", error);
    }
  };

  // Handle tab switch — lazy-load customer detail on first visit
  const handleTabSwitch = (tab: string): void => {
    if (tab === "dettaglio" && !detailFetched) {
      fetchCustomerDetail();
      fetchLookups();
      fetchDefinitions();
    }
  };

  // Calculate fido utilizzato (used credit limit)
  const calculateFidoUtilizzato = (): string => {
    if (!customer?.financialData) return "0 €";

    const total =
      (customer.trustBank?.saldo_contabile || 0) +
      (customer.financialData.bolle_in_attesa_di_fattura || 0) +
      (customer.financialData.ordini || 0) +
      (customer.financialData.titoli || 0) +
      (customer.financialData.esposizione_riba || 0) +
      (customer.financialData.cambiale || 0) +
      (customer.financialData.titoli_a_rischio || 0);

    return total.toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Debounce search whenever inputValue changes
  useEffect(() => {
    const debouncedSearch = debounce((value: string) => {
      performRequest(value);
    }, 500);
    debouncedSearch(inputValue);
  }, [inputValue]);

  const allFieldsAssigned =
    allDefinitions.length > 0 &&
    allDefinitions.every((d) => customerDetailAttrs.some((a) => a.key === d.attribute_key));

  if (globalLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <BeatLoader color="#2563eb" />
      </div>
    );
  }

  if (loading && !customer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="animate-spin material-symbols-outlined md-48">
          progress_activity
        </span>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">
          Nessun dettaglio cliente trovato
        </div>
      </div>
    );
  }

  // --- Main Layout ---
  return (
    <div className={`min-h-screen ${baseClasses.background}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Spacer */}
        <div className="relative sm:max-w-4xl px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 md:mt-50">
          <div className="sm:max-w-4xl px-5 sm:px-12 mx-auto flex-col justify-between mt-cu-md md:mt-20"></div>
        </div>

        {/* Header Section */}
        <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 rounded-full ${baseClasses.hover} transition-colors`}
              aria-label="Torna indietro"
            >
              <span
                className={`material-symbols-outlined ${baseClasses.highlight}`}
              >
                arrow_back
              </span>
            </button>
            <h1 className={`text-3xl font-bold ${baseClasses.cardHeader}`}>
              {customer.ragione_sociale}
            </h1>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex gap-x-2" aria-label="Tabs" role="tablist" aria-orientation="horizontal">
            <button
              type="button"
              className={`hs-tab-active:bg-blue-600 hs-tab-active:text-white py-3 px-6 inline-flex items-center gap-x-2 text-base font-semibold text-center rounded-lg ${baseClasses.hover} ${baseClasses.text} active`}
              id="tab-anagrafica-item"
              aria-selected="true"
              data-hs-tab="#tab-anagrafica"
              aria-controls="tab-anagrafica"
              role="tab"
            >
              Anagrafica
            </button>
            <button
              type="button"
              className={`hs-tab-active:bg-blue-600 hs-tab-active:text-white py-3 px-6 inline-flex items-center gap-x-2 text-base font-semibold text-center rounded-lg ${baseClasses.hover} ${baseClasses.text}`}
              id="tab-dettaglio-item"
              aria-selected="false"
              data-hs-tab="#tab-dettaglio-commerciale"
              aria-controls="tab-dettaglio-commerciale"
              role="tab"
              onClick={() => handleTabSwitch("dettaglio")}
            >
              Dettaglio Commerciale
            </button>
          </nav>
        </div>

        {/* Tab Panel 1: Anagrafica */}
        <div id="tab-anagrafica" role="tabpanel" aria-labelledby="tab-anagrafica-item">

        {/* Primary Info Section */}
        <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Codice Cliente", value: customer.codice_cliente },
              { label: "Codice Agente", value: customer.codice_agente },
              { label: "Telefono", value: customer.telefono_cliente || "-" },
              { label: "Città", value: customer.localita_cliente || "-" },
            ].map((item, index) => (
              <div key={index} className={`${baseClasses.inputBg} p-4`}>
                <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                  {item.label}
                </div>
                <div className={`text-lg font-medium ${baseClasses.highlight}`}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Customer Info (Collapsible) */}
        <div className={`rounded-lg mb-6 border ${baseClasses.card}`}>
          <button
            onClick={() => setCollapseActive(!collapseActive)}
            className={`w-full px-6 py-4 flex items-center justify-between ${baseClasses.hover} transition-colors`}
          >
            <span className={`text-lg font-medium ${baseClasses.cardHeader}`}>
              {collapseActive ? "Meno Informazioni" : "Maggiori Informazioni"}
            </span>
            <span
              className={`material-symbols-outlined ${baseClasses.highlight}`}
            >
              {collapseActive ? "expand_less" : "expand_more"}
            </span>
          </button>
          {collapseActive && (
            <div className={`px-6 py-4 border-t ${baseClasses.border}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Ragione Sociale", value: customer.ragione_sociale },
                  {
                    label: "Indirizzo",
                    value: customer.indirizzo_cliente || "-",
                  },
                  {
                    label: "Provincia",
                    value: customer.provincia_cliente || "-",
                  },
                  { label: "CAP", value: customer.cap_cliente || "-" },
                  {
                    label: "Email",
                    value: customer.email_cliente || "-",
                  },
                  {
                    label: "Modalità di pagamento",
                    value: customer.pagamento || "-",
                  },
                ].map((item, index) => (
                  <div key={index} className={`${baseClasses.inputBg} p-4`}>
                    <div
                      className={`text-sm mb-1 ${baseClasses.textSecondary}`}
                    >
                      {item.label}
                    </div>
                    <div
                      className={`text-lg font-medium ${baseClasses.highlight}`}
                    >
                      {item.value || "-"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Addresses Section */}
        <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
          <h2 className={`text-2xl font-bold mb-4 ${baseClasses.cardHeader}`}>
            Indirizzi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {customer.addresses && customer.addresses.length > 0 ? (
              customer.addresses.map((addr, index) => (
                <div key={index} className={`${baseClasses.inputBg} p-4`}>
                  <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                    Indirizzo {index + 1} (#{addr.codice_indirizzo})
                  </div>
                  <div
                    className={`text-lg font-medium ${baseClasses.highlight}`}
                  >
                    {addr.indirizzo}
                  </div>
                  <div className={`text-sm ${baseClasses.text}`}>
                    {addr.cap}, {addr.citta}, {addr.provincia}
                  </div>
                  <div className={`text-sm ${baseClasses.text}`}>
                    Tel: {addr.tel || "-"}
                  </div>
                  {addr.email && (
                    <div className={`text-sm ${baseClasses.text}`}>
                      Email: {addr.email}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={`${baseClasses.inputBg} p-4`}>
                <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                  Nessun indirizzo disponibile
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Financial Data Section */}
        {customer.trustBank && (
          <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
            <h2 className={`text-2xl font-bold mb-4 ${baseClasses.cardHeader}`}>
              Dati Contabili
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[
                {
                  label: "Saldo Contabile",
                  value: `${(
                    customer.trustBank?.saldo_contabile || 0
                  ).toLocaleString("it-IT")} €`,
                },
                {
                  label: "Fido",
                  value: `${(customer.trustBank?.fido || 0).toLocaleString(
                    "it-IT"
                  )} €`,
                },
                {
                  label: "Fido Utilizzato",
                  value: calculateFidoUtilizzato(),
                },
                {
                  label: "Fido Assicurato",
                  value: customer.financialSituation?.find(
                    (f) => f.tipo === "Fido"
                  )?.valore
                    ? `${customer.financialSituation
                        .find((f) => f.tipo === "Fido")
                        .valore.toLocaleString("it-IT")} €`
                    : "-",
                },
              ].map((item, index) => (
                <div key={index} className={`${baseClasses.inputBg} p-4`}>
                  <div className={`text-sm mb-1 ${baseClasses.textSecondary}`}>
                    {item.label}
                  </div>
                  <div
                    className={`text-lg font-medium ${baseClasses.highlight}`}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
            {/* Additional Financial Details (Collapsible) */}
            <div className="rounded-lg mb-6 border">
              <button
                onClick={() => setSecondCollapseActive(!secondCollapseActive)}
                className={`w-full px-6 py-4 flex items-center justify-between ${baseClasses.hover} transition-colors`}
              >
                <span
                  className={`text-lg font-medium ${baseClasses.cardHeader}`}
                >
                  {secondCollapseActive
                    ? "Meno Informazioni"
                    : "Maggiori Informazioni"}
                </span>
                <span
                  className={`material-symbols-outlined ${baseClasses.highlight}`}
                >
                  {secondCollapseActive ? "expand_less" : "expand_more"}
                </span>
              </button>
              {secondCollapseActive && (
                <div className={`px-6 py-4 border-t ${baseClasses.border}`}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {
                        label: "Bolle Att. Fat.",
                        value: Math.floor(
                          Number(
                            customer.financialData
                              ?.bolle_in_attesa_di_fattura || 0
                          )
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Ordini Programmati",
                        value: Math.floor(
                          Number(
                            customer.financialData?.ordini_programmati || 0
                          )
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Ordini",
                        value: Math.floor(
                          Number(customer.financialData?.ordini || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Esposizione Riba",
                        value: Math.floor(
                          Number(customer.financialData?.esposizione_riba || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Cambiale",
                        value: Math.floor(
                          Number(customer.financialData?.cambiale || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Insoluti",
                        value: Math.floor(
                          Number(customer.financialData?.insoluti || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Titoli a Rischio",
                        value: Math.floor(
                          Number(customer.financialData?.titoli_a_rischio || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                      {
                        label: "Titoli",
                        value: Math.floor(
                          Number(customer.financialData?.titoli || 0)
                        ).toLocaleString("it-IT", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 0,
                        }),
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`rounded-lg p-4 ${baseClasses.inputBg}`}
                      >
                        <div
                          className={`text-sm mb-1 ${baseClasses.textSecondary}`}
                        >
                          {item.label}
                        </div>
                        <div
                          className={`text-lg font-medium ${baseClasses.highlight}`}
                        >
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Discounts Section */}
        <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
          <h2 className={`text-2xl font-bold mb-4 ${baseClasses.cardHeader}`}>
            Scontistiche
          </h2>
          <Formik
            innerRef={formikRef}
            enableReinitialize={true}
            initialValues={{
              search: "",
            }}
            validateOnChange={false}
            onSubmit={() => {}}
          >
            {() => (
              <Field
                name="sconto"
                className={classNames(
                  "py-3 px-4 mt-6 block w-full font-semibold border rounded-none text-lg focus:border-blue-500 focus:ring-blue-500"
                )}
                placeholder="Cerca per descrizione sconto"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setInputValue(e.target.value)
                }
              />
            )}
          </Formik>
          <div className="overflow-x-auto mt-4">
            <table className="w-full min-w-max">
              <thead>
                <tr>
                  {[
                    "Codice Sconto",
                    "Descrizione",
                    "Sconto 1",
                    "Sconto 2",
                    "Sconto 3",
                    "Sconto Effettivo",
                  ].map((header, index) => (
                    <th
                      key={index}
                      className={`px-6 py-3 text-left text-xs font-medium ${baseClasses.textSecondary} uppercase tracking-wider`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDiscounts.length > 0 ? (
                  filteredDiscounts.map((discount, index) => (
                    <tr key={index} className={baseClasses.hover}>
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${baseClasses.text}`}
                      >
                        {discount.codice_sconto}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${baseClasses.text}`}
                      >
                        {discount.descrizione}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-center ${baseClasses.text}`}
                      >
                        {discount.percentuale_sconto_1}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-center ${baseClasses.text}`}
                      >
                        {discount.percentuale_sconto_2}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-center ${baseClasses.text}`}
                      >
                        {discount.percentuale_sconto_3}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-right ${baseClasses.text}`}
                      >
                        {(
                          (1 -
                            (1 - parsePerc(discount.percentuale_sconto_1)) *
                              (1 - parsePerc(discount.percentuale_sconto_2)) *
                              (1 - parsePerc(discount.percentuale_sconto_3))) *
                          100
                        ).toFixed(2)}
                        %
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className={`px-6 py-4 text-center ${baseClasses.text}`}
                    >
                      Nessuna scontistica disponibile
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setPage(page + 1)}
                disabled={loading}
                className={`px-4 py-2 ${baseClasses.hover} rounded-md ${baseClasses.highlight} transition-colors`}
              >
                {loading ? (
                  <span className="animate-spin material-symbols-outlined">
                    progress_activity
                  </span>
                ) : (
                  "Carica altri"
                )}
              </button>
            </div>
          )}
        </div>

        </div>{/* End Tab Panel 1: Anagrafica */}

        {/* Tab Panel 2: Dettaglio Commerciale */}
        <div id="tab-dettaglio-commerciale" className="hidden" role="tabpanel" aria-labelledby="tab-dettaglio-item">
          {detailLoading ? (
            <div className="flex justify-center items-center py-12">
              <BeatLoader color="#2563eb" />
            </div>
          ) : (
            <>
              {/* Right-aligned Buttons */}
              <div className="flex justify-end gap-3 mb-6">
                <button
                  onClick={() => {
                    setShowHistory(true);
                    fetchHistory();
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <span className="material-symbols-outlined text-xl">history</span>
                  Storico
                </button>
                <button
                  onClick={() => {
                    setAddingNew(true);
                    if (allDefinitions.length === 0) fetchDefinitions();
                    if (Object.keys(lookups).length === 0) fetchLookups();
                  }}
                  disabled={allFieldsAssigned}
                  title={allFieldsAssigned ? "Tutti i campi sono già stati aggiunti per questo cliente" : undefined}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${allFieldsAssigned ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                >
                  <span className="material-symbols-outlined text-xl">add</span>
                  Aggiungi attributo
                </button>
              </div>

              {/* History Modal */}
              {showHistory && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setShowHistory(false)}
                  />
                  <div className="relative w-full max-w-2xl mx-4 rounded-lg border shadow-xl flex flex-col max-h-[80vh] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Storico modifiche</h3>
                        <p className="text-sm mt-0.5 text-gray-500 dark:text-gray-400">Cliente {id}</p>
                      </div>
                      <button
                        onClick={() => setShowHistory(false)}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <span className="material-symbols-outlined text-xl text-gray-500 dark:text-gray-400">close</span>
                      </button>
                    </div>

                    {/* Body */}
                    <div className="overflow-y-auto px-6 py-4 flex-1">
                      {historyLoading ? (
                        <div className="flex justify-center py-10">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                        </div>
                      ) : historyData.length === 0 ? (
                        <p className="text-center py-10 text-gray-500 dark:text-gray-400">Nessuna modifica registrata.</p>
                      ) : (
                        <ol className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-3">
                          {historyData.map((entry, idx) => {
                            const isInsert = entry.operation === 'INSERT';
                            const isDelete = entry.operation === 'DELETE';
                            const dotColor = isInsert ? 'bg-green-500' : isDelete ? 'bg-red-500' : 'bg-blue-500';
                            const opLabel = isInsert ? 'Aggiunto' : isDelete ? 'Eliminato' : 'Modificato';
                            const opColor = isInsert
                              ? 'text-green-600 dark:text-green-400'
                              : isDelete
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-blue-600 dark:text-blue-400';
                            const date = new Date(entry.changed_at);
                            const dateStr = date.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
                            const timeStr = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

                            return (
                              <li key={idx} className="mb-8 ml-6">
                                {/* Dot */}
                                <span className={`absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full ${dotColor} ring-4 ring-white dark:ring-gray-900`} />

                                {/* Date + time */}
                                <time className="mb-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                  {dateStr} alle {timeStr}
                                </time>

                                {/* Card */}
                                <div className="mt-1 p-3 rounded-lg border bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs font-bold uppercase tracking-wide ${opColor}`}>{opLabel}</span>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {entry.attribute_label || entry.attribute_key}
                                    </span>
                                  </div>

                                  {!isInsert && !isDelete && (
                                    <div className="flex flex-col gap-1 text-sm">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">Da:</span>
                                        <span className="line-through text-gray-500 dark:text-gray-400">{entry.old_value ?? '—'}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">A:</span>
                                        <span className="font-medium text-gray-900 dark:text-gray-100">{entry.new_value ?? '—'}</span>
                                      </div>
                                    </div>
                                  )}
                                  {isInsert && (
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                      Valore: <span className="font-medium">{entry.new_value ?? '—'}</span>
                                    </p>
                                  )}
                                  {isDelete && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                      {entry.old_value ?? '—'}
                                    </p>
                                  )}

                                  <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
                                    da <span className="font-medium">{entry.changed_by || 'utente sconosciuto'}</span>
                                  </p>
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Add Attribute Modal */}
              {addingNew && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => {
                      setAddingNew(false);
                      setNewAttrKey("");
                      setNewAttrValue("");
                    }}
                  />
                  <div className={`bg-white dark:bg-black relative w-full max-w-lg mx-4 rounded-lg p-6 border shadow-xl ${baseClasses.card}`}>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className={`text-lg font-semibold ${baseClasses.cardHeader}`}>
                        Nuovo Attributo
                      </h3>
                      <button
                        onClick={() => {
                          setAddingNew(false);
                          setNewAttrKey("");
                          setNewAttrValue("");
                        }}
                        className={`p-1.5 rounded ${baseClasses.hover} transition-colors`}
                      >
                        <span className={`material-symbols-outlined text-xl ${baseClasses.textSecondary}`}>close</span>
                      </button>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${baseClasses.textSecondary}`}>
                          Campo
                        </label>
                        <ReactSelect
                          value={
                            newAttrKey
                              ? {
                                  value: newAttrKey,
                                  label: allDefinitions.find((d) => d.attribute_key === newAttrKey)?.attribute_label || newAttrKey,
                                }
                              : null
                          }
                          onChange={(opt) => {
                            setNewAttrKey(opt?.value || "");
                            setNewAttrValue("");
                          }}
                          options={allDefinitions
                            .filter((d) => !customerDetailAttrs.some((a) => a.key === d.attribute_key))
                            .map((d) => ({ value: d.attribute_key, label: d.attribute_label }))}
                          isSearchable
                          placeholder="Seleziona campo..."
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                              borderColor: theme === "dark" ? "#374151" : "#d1d5db",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: theme === "dark" ? "#d1d5db" : "#111827",
                            }),
                            input: (base) => ({
                              ...base,
                              color: theme === "dark" ? "#d1d5db" : "#111827",
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                              zIndex: 9999,
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused
                                ? theme === "dark" ? "#374151" : "#eff6ff"
                                : "transparent",
                              color: theme === "dark" ? "#d1d5db" : "#111827",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      </div>

                      {newAttrKey && (() => {
                        const def = allDefinitions.find((d) => d.attribute_key === newAttrKey);
                        if (!def) return null;
                        return (
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${baseClasses.textSecondary}`}>
                              Valore
                            </label>
                            {def.attribute_type === "dropdown" && def.lookup_table && lookups[def.lookup_table] ? (
                              <ReactSelect
                                value={
                                  newAttrValue
                                    ? {
                                        value: newAttrValue,
                                        label: lookups[def.lookup_table]?.find((o) => String(o.id) === newAttrValue)?.value || newAttrValue,
                                      }
                                    : null
                                }
                                onChange={(opt) => setNewAttrValue(opt?.value || "")}
                                options={(lookups[def.lookup_table] || []).map((o) => ({
                                  value: String(o.id),
                                  label: o.value,
                                }))}
                                isSearchable
                                placeholder="Seleziona..."
                                menuPortalTarget={document.body}
                                styles={{
                                  control: (base) => ({
                                    ...base,
                                    backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                                    borderColor: theme === "dark" ? "#374151" : "#d1d5db",
                                  }),
                                  singleValue: (base) => ({
                                    ...base,
                                    color: theme === "dark" ? "#d1d5db" : "#111827",
                                  }),
                                  input: (base) => ({
                                    ...base,
                                    color: theme === "dark" ? "#d1d5db" : "#111827",
                                  }),
                                  menu: (base) => ({
                                    ...base,
                                    backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                                    zIndex: 9999,
                                  }),
                                  option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isFocused
                                      ? theme === "dark" ? "#374151" : "#eff6ff"
                                      : "transparent",
                                    color: theme === "dark" ? "#d1d5db" : "#111827",
                                  }),
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                              />
                            ) : (
                              <input
                                type={def.attribute_type === "numeric" ? "number" : "text"}
                                value={newAttrValue}
                                onChange={(e) => setNewAttrValue(e.target.value)}
                                className={`w-full py-2 px-3 border rounded-lg text-sm ${baseClasses.border} ${baseClasses.text} ${baseClasses.inputBg}`}
                                placeholder={def.attribute_type === "numeric" ? "Inserisci numero..." : "Inserisci testo..."}
                              />
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={async () => {
                            if (newAttrKey && newAttrValue) {
                              await saveAttribute(newAttrKey, newAttrValue);
                              setAddingNew(false);
                              setNewAttrKey("");
                              setNewAttrValue("");
                            }
                          }}
                          disabled={!newAttrKey || !newAttrValue}
                          className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="material-symbols-outlined text-xl">check</span>
                          Salva
                        </button>
                        <button
                          onClick={() => {
                            setAddingNew(false);
                            setNewAttrKey("");
                            setNewAttrValue("");
                          }}
                          className={`flex items-center gap-1 px-4 py-2 rounded-lg border ${baseClasses.border} ${baseClasses.text} ${baseClasses.hover} transition-colors`}
                        >
                          <span className="material-symbols-outlined text-xl">close</span>
                          Annulla
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Grouped Attribute Cards */}
              {customerDetailAttrs.length === 0 && detailFetched ? (
                <div className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
                  <p className={`text-center ${baseClasses.textSecondary}`}>
                    Nessun dettaglio commerciale disponibile per questo cliente.
                  </p>
                </div>
              ) : (
                ATTRIBUTE_GROUPS.map((group) => {
                  const groupAttrs = group.keys
                    .map((key) => customerDetailAttrs.find((a) => a.key === key))
                    .filter((a): a is CustomerDetailAttribute => a !== undefined);
                  if (groupAttrs.length === 0) return null;
                  return (
                    <div key={group.label} className={`rounded-lg p-6 mb-6 border ${baseClasses.card}`}>
                      <h2 className={`text-xl font-bold mb-4 ${baseClasses.cardHeader}`}>
                        {group.label}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupAttrs.map((attr) => (
                          <div
                            key={attr.key}
                            className={`rounded-lg p-4 border ${baseClasses.border} ${baseClasses.inputBg}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className={`text-sm font-medium ${baseClasses.textSecondary}`}>
                                {attr.label}
                              </div>
                              <div className="flex items-center gap-2">
                                {editingKey !== attr.key && (
                                  <>
                                    <button
                                      onClick={() => {
                                        setEditingKey(attr.key);
                                        setEditValue(attr.value || "");
                                      }}
                                      className={`p-1.5 rounded ${baseClasses.hover} transition-colors`}
                                      title="Modifica"
                                      disabled={savingKeys.has(attr.key)}
                                    >
                                      <span className={`material-symbols-outlined text-xl ${baseClasses.highlight}`}>
                                        edit
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Eliminare "${attr.label}"?`)) {
                                          deleteAttribute(attr.key);
                                        }
                                      }}
                                      className="p-1.5 rounded hover:bg-red-50 transition-colors"
                                      title="Elimina"
                                      disabled={savingKeys.has(attr.key)}
                                    >
                                      <span className="material-symbols-outlined text-xl text-red-500">
                                        delete
                                      </span>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {editingKey === attr.key ? (
                              <div className="flex items-center gap-2">
                                {attr.type === "dropdown" && attr.lookup_table && lookups[attr.lookup_table] ? (
                                  <div className="flex-1">
                                    <ReactSelect
                                      value={
                                        editValue
                                          ? {
                                              value: editValue,
                                              label: lookups[attr.lookup_table!]?.find((o) => String(o.id) === editValue)?.value || editValue,
                                            }
                                          : null
                                      }
                                      onChange={(opt) => setEditValue(opt?.value || "")}
                                      options={(lookups[attr.lookup_table!] || []).map((o) => ({
                                        value: String(o.id),
                                        label: o.value,
                                      }))}
                                      isSearchable
                                      placeholder="Seleziona..."
                                      menuPortalTarget={document.body}
                                      styles={{
                                        control: (base) => ({
                                          ...base,
                                          backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                                          borderColor: theme === "dark" ? "#374151" : "#d1d5db",
                                          minHeight: "32px",
                                        }),
                                        singleValue: (base) => ({
                                          ...base,
                                          color: theme === "dark" ? "#d1d5db" : "#111827",
                                          fontSize: "0.875rem",
                                        }),
                                        input: (base) => ({
                                          ...base,
                                          color: theme === "dark" ? "#d1d5db" : "#111827",
                                          fontSize: "0.875rem",
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          backgroundColor: theme === "dark" ? "#1f2937" : "#fff",
                                          zIndex: 9999,
                                        }),
                                        option: (base, state) => ({
                                          ...base,
                                          backgroundColor: state.isFocused
                                            ? theme === "dark" ? "#374151" : "#eff6ff"
                                            : "transparent",
                                          color: theme === "dark" ? "#d1d5db" : "#111827",
                                          fontSize: "0.875rem",
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type={attr.type === "numeric" ? "number" : "text"}
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className={`flex-1 py-1 px-2 border rounded text-sm ${baseClasses.border} ${baseClasses.text} ${baseClasses.inputBg}`}
                                    autoFocus
                                  />
                                )}
                                <button
                                  onClick={() => saveAttribute(attr.key, editValue)}
                                  className="p-1.5 rounded hover:bg-green-50 transition-colors"
                                  disabled={savingKeys.has(attr.key)}
                                  title="Salva"
                                >
                                  {savingKeys.has(attr.key) ? (
                                    <span className="animate-spin material-symbols-outlined text-xl text-blue-500">
                                      progress_activity
                                    </span>
                                  ) : (
                                    <span className="material-symbols-outlined text-xl text-green-600">
                                      check
                                    </span>
                                  )}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingKey(null);
                                    setEditValue("");
                                  }}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  title="Annulla"
                                >
                                  <span className={`material-symbols-outlined text-xl ${baseClasses.textSecondary}`}>
                                    close
                                  </span>
                                </button>
                              </div>
                            ) : (
                              <div className={`text-lg font-medium ${baseClasses.highlight}`}>
                                {savingKeys.has(attr.key) ? (
                                  <span className="animate-spin material-symbols-outlined text-xl">
                                    progress_activity
                                  </span>
                                ) : (
                                  attr.resolved_value || attr.value || "-"
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>{/* End Tab Panel 2: Dettaglio Commerciale */}

      </div>
    </div>
  );
}
